import { useEffect } from 'react';
import SEOHead from '../../components/feature/SEOHead';

export default function About() {
  const baseUrl = import.meta.env.VITE_SITE_URL || 'https://crownabundance.com';
  
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    'name': 'About The CROWN Abundance Movement',
    'description': 'Discover The CROWN Abundance Movement vision, mission, and core philosophy. Learn about our collaborative approach to wealth mastery, relationship wholeness, and generational legacy building.',
    'url': `${baseUrl}/about`,
    'mainEntity': {
      '@type': 'Organization',
      'name': 'The CROWN Abundance Movement',
      'alternateName': 'House of CROWN',
      'description': 'A global empowerment movement focused on wealth mastery, relationship wholeness, and generational legacy',
      'url': baseUrl,
      'foundingDate': '2020',
      'mission': 'To empower and unite entrepreneurs, founders, investors, families, and youth around a shared journey of abundance — financially, relationally, and spiritually — anchored in values and practical tools.'
    }
  };

  const philosophies = [
    {
      icon: 'ri-shield-check-line',
      title: 'Kingdom Values',
      description: 'We operate from timeless principles that honor integrity, stewardship, and purpose-driven success.',
      gradient: 'from-amber-400 to-orange-500'
    },
    {
      icon: 'ri-team-line',
      title: 'Collaborative Community',
      description: 'We believe in the power of unity, where diverse voices come together to create exponential impact.',
      gradient: 'from-blue-400 to-indigo-500'
    },
    {
      icon: 'ri-lightbulb-flash-line',
      title: 'Practical Wisdom',
      description: 'We provide actionable tools and strategies that bridge spiritual truth with real-world application.',
      gradient: 'from-emerald-400 to-teal-500'
    },
    {
      icon: 'ri-heart-pulse-line',
      title: 'Holistic Transformation',
      description: 'We address the whole person—mind, body, spirit, relationships, and finances—for complete restoration.',
      gradient: 'from-rose-400 to-pink-500'
    },
    {
      icon: 'ri-trophy-line',
      title: 'Excellence & Impact',
      description: 'We pursue excellence in everything we do, creating lasting impact that transforms communities and nations.',
      gradient: 'from-purple-400 to-violet-500'
    },
    {
      icon: 'ri-global-line',
      title: 'Generational Mindset',
      description: 'We think beyond ourselves, building systems and legacies that benefit future generations.',
      gradient: 'from-yellow-400 to-amber-500'
    }
  ];

  return (
    <>
      <SEOHead
        title="About The CROWN Abundance Movement | Vision Mission Values | House of CROWN"
        description="Discover The CROWN Abundance Movement vision, mission, and core philosophy. Learn about our collaborative approach to wealth mastery, relationship wholeness, and generational legacy building through House of CROWN."
        keywords="CROWN Abundance Movement, House of CROWN, wealth mastery, relationship wholeness, generational legacy, collaborative restoration, financial dominion"
        canonicalPath="/about"
        type="AboutPage"
        jsonLd={jsonLd}
      />
      <div className="min-h-screen" style={{ background: '#3D2C8D' }}>
        {/* Hero Section with Background Image */}
        <section className="relative py-32 overflow-hidden min-h-[600px] flex items-center">
          {/* Background Image with Overlay */}
          <div className="absolute inset-0">
            <img 
              src="https://public.readdy.ai/ai/img_res/ed46aaee928c4cbc8e1dc0552d0d8dcb.jpg"
              alt="CROWN Movement Community"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-purple-900/95 via-purple-900/85 to-purple-900/70"></div>
          </div>

          {/* Animated background elements */}
          <div className="absolute inset-0">
            <div className="absolute top-20 left-10 w-32 h-32 bg-gradient-to-r from-amber-400/20 to-yellow-300/20 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute top-40 right-20 w-24 h-24 bg-gradient-to-r from-amber-500/15 to-yellow-400/15 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '1s' }}></div>
            <div className="absolute bottom-32 left-1/3 w-40 h-40 bg-gradient-to-r from-amber-300/10 to-yellow-200/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 text-center">
            <div className="mb-8">
              <div className="inline-block p-4 rounded-full bg-gradient-to-r from-amber-400/20 to-yellow-300/20 backdrop-blur-sm border border-amber-400/30 mb-6 animate-bounce">
                <i className="ri-crown-line text-4xl" style={{ color: '#F5A623' }}></i>
              </div>
            </div>
            
            <h1 className="text-5xl lg:text-7xl font-bold mb-8 animate-fade-in" style={{ fontFamily: '"Playfair Display", serif', color: '#F5A623' }}>
              About The
              <span className="block text-6xl lg:text-8xl bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent">
                CROWN
              </span>
              <span className="block text-4xl lg:text-5xl" style={{ color: '#FFFFFF' }}>
                Movement
              </span>
            </h1>
            
            <div className="text-2xl font-semibold mb-6" style={{ color: '#F5A623' }}>
              Collaborative Restoration Of Wealth and Nations
            </div>
            
            <p className="text-xl max-w-4xl mx-auto leading-relaxed mb-12" style={{ color: '#FFFFFF' }}>
              House of CROWN represents a global movement dedicated to transforming lives through the mastery of wealth, relationships, and generational legacy.
            </p>
          </div>
        </section>

        {/* Vision Section with Image */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold mb-6" style={{ fontFamily: '"Playfair Display", serif', color: '#F5A623' }}>
                Our Vision
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-amber-400 to-amber-600 mx-auto rounded-full mb-8"></div>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-r from-amber-400/20 to-yellow-300/20 rounded-3xl blur-xl group-hover:blur-2xl transition-all duration-500"></div>
                <img 
                  src="https://readdy.ai/api/search-image?query=Inspiring%20vision%20of%20diverse%20African%20American%20and%20Latino%20families%20celebrating%20financial%20success%20and%20unity%2C%20multi-generational%20gathering%20in%20elegant%20modern%20home%20with%20warm%20golden%20sunset%20lighting%20streaming%20through%20large%20windows%2C%20joyful%20expressions%2C%20luxury%20interior%20design%2C%20symbols%20of%20wealth%20and%20legacy%2C%20deep%20purple%20and%20gold%20color%20palette%2C%20professional%20photography%2C%20emotional%20connection%20and%20prosperity%2C%20no%20white%20people%2C%20only%20Black%20and%20Latino%20families&width=600&height=500&seq=vision-image-v2&orientation=portrait"
                  alt="Vision of Financial Dominion"
                  className="relative rounded-3xl shadow-2xl w-full h-[500px] object-cover transform group-hover:scale-105 transition-transform duration-500"
                />
              </div>

              <div className="relative rounded-3xl p-12 border border-amber-400/30 shadow-2xl" style={{ background: 'linear-gradient(135deg, rgba(245, 166, 35, 0.1) 0%, rgba(61, 44, 141, 0.8) 50%, rgba(245, 166, 35, 0.1) 100%)' }}>
                <div className="text-center">
                  <blockquote className="text-3xl lg:text-4xl font-bold leading-relaxed mb-8" style={{ fontFamily: '"Playfair Display", serif', color: '#FFFFFF' }}>
                    "A world where individuals, families, and communities walk in 
                    <span style={{ color: '#F5A623' }}> financial dominion</span>, 
                    <span style={{ color: '#F5A623' }}> relational wholeness</span>, and 
                    <span style={{ color: '#F5A623' }}> generational legacy</span>."
                  </blockquote>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center p-8 rounded-xl bg-white/5 backdrop-blur-sm transform hover:scale-105 transition-all duration-300 hover:shadow-2xl border border-amber-400/20">
                <div className="w-20 h-20 bg-gradient-to-r from-amber-400 to-yellow-300 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <i className="ri-money-dollar-circle-line text-4xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold mb-2" style={{ color: '#F5A623' }}>Financial Dominion</h3>
                <p className="text-sm" style={{ color: '#FFFFFF' }}>Mastery over money as a tool for purpose and impact</p>
              </div>
              <div className="text-center p-8 rounded-xl bg-white/5 backdrop-blur-sm transform hover:scale-105 transition-all duration-300 hover:shadow-2xl border border-amber-400/20">
                <div className="w-20 h-20 bg-gradient-to-r from-rose-400 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <i className="ri-heart-line text-4xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold mb-2" style={{ color: '#F5A623' }}>Relational Wholeness</h3>
                <p className="text-sm" style={{ color: '#FFFFFF' }}>Healthy connections that fuel personal and professional growth</p>
              </div>
              <div className="text-center p-8 rounded-xl bg-white/5 backdrop-blur-sm transform hover:scale-105 transition-all duration-300 hover:shadow-2xl border border-amber-400/20">
                <div className="w-20 h-20 bg-gradient-to-r from-emerald-400 to-teal-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <i className="ri-parent-line text-4xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold mb-2" style={{ color: '#F5A623' }}>Generational Legacy</h3>
                <p className="text-sm" style={{ color: '#FFFFFF' }}>Impact that transcends individual success to benefit future generations</p>
              </div>
            </div>
          </div>
        </section>

        {/* Mission Section with Image */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold mb-6" style={{ fontFamily: '"Playfair Display", serif', color: '#F5A623' }}>
                Our Mission
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-amber-400 to-amber-600 mx-auto rounded-full mb-8"></div>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-12">
              <div className="relative rounded-3xl p-12 border border-amber-400/30 shadow-2xl order-2 lg:order-1" style={{ background: 'linear-gradient(135deg, rgba(245, 166, 35, 0.15) 0%, rgba(61, 44, 141, 0.9) 50%, rgba(245, 166, 35, 0.15) 100%)' }}>
                <div className="text-center">
                  <blockquote className="text-2xl lg:text-3xl leading-relaxed" style={{ color: '#FFFFFF' }}>
                    "To empower and unite entrepreneurs, founders, investors, families, and youth around a shared journey of abundance — financially, relationally, and spiritually — anchored in values and practical tools."
                  </blockquote>
                </div>
              </div>

              <div className="relative group order-1 lg:order-2">
                <div className="absolute inset-0 bg-gradient-to-r from-purple-400/20 to-indigo-300/20 rounded-3xl blur-xl group-hover:blur-2xl transition-all duration-500"></div>
                <img 
                  src="https://readdy.ai/api/search-image?query=Powerful%20scene%20of%20diverse%20African%20American%20and%20Latino%20business%20leaders%20and%20entrepreneurs%20collaborating%20around%20elegant%20conference%20table%20in%20luxury%20boardroom%2C%20strategic%20planning%20session%2C%20modern%20technology%20displays%2C%20warm%20professional%20lighting%2C%20symbols%20of%20unity%20and%20empowerment%2C%20deep%20purple%20and%20gold%20accents%2C%20high-end%20corporate%20photography%2C%20leadership%20and%20collaboration%20theme%2C%20no%20white%20people%2C%20only%20Black%20and%20Latino%20professionals&width=600&height=500&seq=mission-image-v2&orientation=portrait"
                  alt="Mission of Empowerment"
                  className="relative rounded-3xl shadow-2xl w-full h-[500px] object-cover transform group-hover:scale-105 transition-transform duration-500"
                />
              </div>
            </div>

            {/* Three Pillars - Redesigned */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-br from-amber-400/20 to-orange-500/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div className="relative p-8 rounded-2xl bg-white/5 backdrop-blur-sm border border-amber-400/30 transform hover:-translate-y-2 transition-all duration-300">
                  <div className="w-16 h-16 bg-gradient-to-r from-amber-400 to-orange-500 rounded-xl flex items-center justify-center mx-auto mb-6 shadow-lg transform group-hover:scale-110 transition-transform duration-300">
                    <i className="ri-crown-line text-3xl text-white"></i>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold mb-2 bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent">Restoring</div>
                    <div className="text-xl text-gray-200 mb-3">Dominion</div>
                    <p className="text-sm text-gray-300 leading-relaxed">
                      Reclaiming authority over finances, relationships, and purpose
                    </p>
                  </div>
                </div>
              </div>

              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-400/20 to-teal-500/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div className="relative p-8 rounded-2xl bg-white/5 backdrop-blur-sm border border-amber-400/30 transform hover:-translate-y-2 transition-all duration-300">
                  <div className="w-16 h-16 bg-gradient-to-r from-emerald-400 to-teal-500 rounded-xl flex items-center justify-center mx-auto mb-6 shadow-lg transform group-hover:scale-110 transition-transform duration-300">
                    <i className="ri-parent-line text-3xl text-white"></i>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold mb-2 bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent">Multiplying</div>
                    <div className="text-xl text-gray-200 mb-3">Legacy</div>
                    <p className="text-sm text-gray-300 leading-relaxed">
                      Building generational impact that transcends time
                    </p>
                  </div>
                </div>
              </div>

              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-400/20 to-indigo-500/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div className="relative p-8 rounded-2xl bg-white/5 backdrop-blur-sm border border-amber-400/30 transform hover:-translate-y-2 transition-all duration-300">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-400 to-indigo-500 rounded-xl flex items-center justify-center mx-auto mb-6 shadow-lg transform group-hover:scale-110 transition-transform duration-300">
                    <i className="ri-money-dollar-circle-line text-3xl text-white"></i>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold mb-2 bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent">Building</div>
                    <div className="text-xl text-gray-200 mb-3">Wealth</div>
                    <p className="text-sm text-gray-300 leading-relaxed">
                      Creating sustainable prosperity with purpose and integrity
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Core Beliefs Section */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold mb-6" style={{ fontFamily: '"Playfair Display", serif', color: '#F5A623' }}>
                Core Beliefs
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-amber-400 to-amber-600 mx-auto rounded-full mb-8"></div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
              {philosophies.map((philosophy, index) => (
                <div 
                  key={index} 
                  className="group relative rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border border-amber-400/20"
                  style={{ 
                    background: 'rgba(255, 255, 255, 0.05)',
                    backdropFilter: 'blur(10px)',
                    animationDelay: `${index * 0.1}s`
                  }}
                >
                  <div className={`absolute inset-0 bg-gradient-to-r ${philosophy.gradient} rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300`}></div>
                  
                  <div className="relative mb-6">
                    <div className={`w-20 h-20 bg-gradient-to-r ${philosophy.gradient} rounded-2xl flex items-center justify-center mx-auto shadow-lg transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-300`}>
                      <i className={`${philosophy.icon} text-3xl text-white`}></i>
                    </div>
                  </div>

                  <div className="text-center">
                    <h4 className="text-xl font-bold mb-3" style={{ color: '#F5A623' }}>
                      {philosophy.title}
                    </h4>
                    <p className="font-semibold leading-relaxed" style={{ color: '#FFFFFF' }}>
                      {philosophy.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Enhanced Guiding Principles Section */}
        <section className="relative py-24 overflow-hidden" style={{ background: 'linear-gradient(135deg, #1a0b3d 0%, #2d1b69 50%, #1a0b3d 100%)' }}>
          {/* Animated Background Elements */}
          <div className="absolute inset-0 overflow-hidden opacity-30">
            <div className="absolute top-20 left-10 w-64 h-64 bg-gradient-to-r from-amber-400/20 to-yellow-300/20 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-20 right-10 w-80 h-80 bg-gradient-to-r from-amber-500/15 to-yellow-400/15 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1.5s' }}></div>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '3s' }}></div>
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
            {/* Section Header */}
            <div className="text-center mb-20">
              <div className="inline-block mb-6">
                <div className="flex items-center justify-center space-x-3 px-6 py-3 rounded-full bg-gradient-to-r from-amber-400/20 to-yellow-300/20 backdrop-blur-sm border border-amber-400/30">
                  <i className="ri-compass-3-line text-2xl text-amber-400"></i>
                  <span className="text-amber-400 font-semibold text-sm tracking-wider uppercase">Our Foundation</span>
                </div>
              </div>
              <h2 className="text-5xl lg:text-6xl font-bold mb-6 text-white" style={{ fontFamily: '"Playfair Display", serif' }}>
                Guiding <span className="bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent">Principles</span>
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
                The timeless values that shape our movement and guide every decision we make
              </p>
            </div>

            {/* Principles Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
              {[
                {
                  icon: 'ri-crown-line',
                  title: 'Restoring Dominion',
                  description: 'Reclaiming authority over finances, relationships, and purpose',
                  color: 'from-amber-400 to-orange-500',
                  detail: 'We believe in empowering individuals to take full ownership of their destiny, breaking free from limitations and stepping into their rightful place of influence.'
                },
                {
                  icon: 'ri-parent-line',
                  title: 'Multiplying Legacy',
                  description: 'Building generational impact that transcends time',
                  color: 'from-emerald-400 to-teal-500',
                  detail: 'True success is measured not by what we accumulate, but by what we pass on. We focus on creating lasting value for future generations.'
                },
                {
                  icon: 'ri-money-dollar-circle-line',
                  title: 'Building Wealth',
                  description: 'Creating sustainable prosperity with purpose and integrity',
                  color: 'from-blue-400 to-indigo-500',
                  detail: 'Wealth is a tool for transformation. We teach principles of financial stewardship that create abundance while serving a greater mission.'
                },
                {
                  icon: 'ri-heart-pulse-line',
                  title: 'Healing Relationships',
                  description: 'Restoring wholeness in every connection that matters',
                  color: 'from-rose-400 to-pink-500',
                  detail: 'Strong relationships are the foundation of lasting success. We prioritize emotional intelligence, authentic connection, and relational restoration.'
                },
                {
                  icon: 'ri-team-line',
                  title: 'Empowering Generations',
                  description: 'Equipping youth and families for extraordinary futures',
                  color: 'from-purple-400 to-violet-500',
                  detail: 'We invest in the next generation through mentorship, education, and practical tools that prepare them to lead with wisdom and purpose.'
                },
                {
                  icon: 'ri-lightbulb-flash-line',
                  title: 'Abundance with Purpose',
                  description: 'Aligning prosperity with values and kingdom principles',
                  color: 'from-yellow-400 to-amber-500',
                  detail: 'Every blessing carries responsibility. We cultivate abundance that serves others, honors values, and creates positive change in the world.'
                }
              ].map((principle, index) => (
                <div
                  key={index}
                  className="group relative rounded-3xl p-8 bg-slate-900/60 backdrop-blur-sm border-2 border-amber-400/20 hover:border-amber-400/50 transition-all duration-500 transform hover:-translate-y-2 hover:shadow-2xl overflow-hidden"
                  style={{
                    animationDelay: `${index * 100}ms`,
                    animation: 'fadeInUp 0.8s ease-out forwards'
                  }}
                >
                  {/* Gradient Overlay on Hover */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${principle.color} opacity-0 group-hover:opacity-10 transition-opacity duration-500`}></div>
                  
                  {/* Icon Container */}
                  <div className="relative mb-6">
                    <div className={`w-20 h-20 bg-gradient-to-br ${principle.color} rounded-2xl flex items-center justify-center shadow-lg transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-500`}>
                      <i className={`${principle.icon} text-3xl text-white`}></i>
                    </div>
                    {/* Floating accent */}
                    <div className="absolute -top-2 -right-2 w-6 h-6 bg-amber-400/60 rounded-full opacity-0 group-hover:opacity-100 animate-ping transition-opacity duration-300"></div>
                  </div>

                  {/* Content */}
                  <div className="relative">
                    <h3 className="text-2xl font-bold mb-3 text-white group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:bg-clip-text group-hover:from-amber-400 group-hover:to-yellow-300 transition-all duration-300">
                      {principle.title}
                    </h3>
                    <p className="text-amber-400 font-semibold mb-4 leading-relaxed">
                      {principle.description}
                    </p>
                    <p className="text-sm text-gray-300 leading-relaxed opacity-0 max-h-0 group-hover:opacity-100 group-hover:max-h-40 transition-all duration-500 overflow-hidden">
                      {principle.detail}
                    </p>
                  </div>

                  {/* Bottom Accent Line */}
                  <div className={`absolute bottom-0 left-0 w-0 h-1 bg-gradient-to-r ${principle.color} group-hover:w-full transition-all duration-700`}></div>
                </div>
              ))}
            </div>

            {/* Bottom Statement */}
            <div className="relative">
              <div className="rounded-3xl p-12 bg-gradient-to-br from-slate-900/80 to-slate-800/80 backdrop-blur-sm border-2 border-amber-400/30 shadow-2xl">
                <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
                  <div className="flex-1 text-center lg:text-left">
                    <h3 className="text-3xl lg:text-4xl font-bold mb-4 bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent" style={{ fontFamily: '"Playfair Display", serif' }}>
                      Leadership with Legacy
                    </h3>
                    <p className="text-xl text-gray-200 leading-relaxed">
                      We don't just build leaders — we cultivate legacy-makers who transform communities, restore nations, and create lasting impact for generations to come.
                    </p>
                  </div>
                  <div className="flex-shrink-0">
                    <div className="w-32 h-32 bg-gradient-to-br from-amber-400 to-yellow-300 rounded-full flex items-center justify-center shadow-2xl transform hover:scale-110 transition-transform duration-300">
                      <i className="ri-trophy-line text-5xl text-white"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
